import { useEffect, useMemo, useRef } from "react";
import { useApiClient } from "@/api/_client";
import { createPublicWorkspacesApi } from "@/api/public-workspaces";
import { takePendingAutoJoin } from "@/lib/pending-auto-join";

/**
 * After a lurker signs up/in from a public-workspace landing — which stashed a
 * pending-auto-join slug — fire the auto-join once. On success the new
 * `workspace_members` row syncs via Zero and the existing workspace-scope
 * pinning transitions the user into the community, so no manual scope change
 * is needed here. Failures (409 wrong policy / 403 domain) fall back silently:
 * the user just lands on the splash's Join button.
 */
export function useAutoJoinAfterAuth(userId: string | null | undefined): void {
  const apiClient = useApiClient();
  const api = useMemo(() => createPublicWorkspacesApi(apiClient), [apiClient]);
  // Per-mount guard so the join fires at most once even if the effect re-runs
  // (api identity change, Strict-Mode double-invoke). A remount can't re-fire
  // either: takePendingAutoJoin already consumed the one-shot slug.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!userId || firedRef.current) return;
    const slug = takePendingAutoJoin();
    if (!slug) return;
    firedRef.current = true;
    void api.autoJoin(slug).catch((err) => {
      // Not fatal — the public-workspace splash still offers a manual Join.
      console.warn("[auto-join] post-auth auto-join failed", err);
    });
  }, [userId, api]);
}
